import React, { useState } from 'react';
import PropTypes from 'prop-types';
import axios from 'axios';
import { Formik } from 'formik';
import _ from 'lodash';
import * as Yup from 'yup';

import Button from '~/components/core/Atomic/Buttons/Button';
import Grid from '~/components/core/Atomic/Grid/Grid';
import MenuItem from '~/components/core/Atomic/MenuItem';

import { reportAxiosError } from '../../Utils';
import CardDialog from '../CardDialog';
import MgmFnolSearchResults from '../ClientSpecific/Mgm/MgmFnolSearchResults';
import LoadingIndicator from '../LoadingIndicator';
import useOrganization from '../OrganizationContext';
import SocialSecurityNumberFormat from '../SocialSecurityNumberFormat';
import TextFieldFormik, { DatePickerTextFieldFormik } from '../TextFieldFormik';

import { useStyles } from '../../assets/styles';

function WCFnolSearchScreen({ onReportNewFnol }) {
  const { classes } = useStyles();
  const [showResults, setShowResults] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [foundClaims, setFoundClaims] = useState([]);
  const [searchValues, setSearchValues] = useState(undefined);

  const handleSearch = async (values) => {
    setSearchValues(values);
    try {
      setIsLoading(true);
      const res = await axios.get('/api/v1/wc_claims', { params: values });
      setFoundClaims(res.data.claims);
      setShowResults(true);
    } catch (error) {
      reportAxiosError(error);
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <>
      <div className={classes.cardDiv}>
        <WCClaimsSearch onSubmit={async (values) => await handleSearch(values)} />
      </div>
      {isLoading && <LoadingIndicator />}
      {showResults && (
        <div className={classes.cardDivRow}>
          <MgmFnolSearchResults
            claimType="wc_claim"
            foundClaims={foundClaims}
            onReportNewFnol={async () => await onReportNewFnol(searchValues)}
            shouldHideType
          />
        </div>
      )}
    </>
  );
}

WCFnolSearchScreen.propTypes = {
  onReportNewFnol: PropTypes.func.isRequired,
};

function WCClaimsSearch({ onSubmit }) {
  const { classes } = useStyles();
  const { insuredPropertiesAndLocationsDict } = useOrganization();

  // TODO: remove the filtering once all abbreviations will be valid
  const insuredPropertiesOptions = _.sortBy(
    Object.keys(insuredPropertiesAndLocationsDict),
    (k) => insuredPropertiesAndLocationsDict[k].desc
  ).filter((k) => !!insuredPropertiesAndLocationsDict[k].insured_property_extra.property_abbreviation);

  return (
    <Formik
      initialValues={{
        employee_external_id: '',
        employee_name: '',
        incident_date: '',
        property_id: '',
        employee_social_security_number: '',
      }}
      validationSchema={Yup.object().shape({
        employee_external_id: Yup.number()
          .typeError('Must be a number')
          .integer('Must be a number')
          .positive('Must be a number'),
        employee_name: Yup.string(),
        property_id: Yup.number(),
        incident_date: Yup.date().required('Required'),
        employee_social_security_number: Yup.string().matches(
          /[\d]{9}/,
          'Social security number must be a 9 digit number'
        ),
      })}
      onSubmit={async (values, { setSubmitting }) => {
        try {
          await onSubmit(values);
        } catch (error) {
          setSubmitting(false);
        }
      }}
    >
      {({ handleSubmit, isSubmitting }) => (
        <Grid container justify="center" alignItems="center">
          <Grid item xs={12} md={11} lg={10}>
            <CardDialog noCardTitle>
              <div style={{ height: '70px' }}>
                <Grid
                  container
                  justify="center"
                  alignContent="center"
                  wrap="nowrap"
                  spacing={1}
                  style={{ height: '100%' }}
                >
                  <Grid item xs={3}>
                    <DatePickerTextFieldFormik
                      id="incident_date"
                      label="Incident Date"
                      className={classes.textField}
                      disableFuture
                      fullWidth
                      disabled={isSubmitting}
                      clearable
                    />
                  </Grid>

                  <Grid item xs={3}>
                    <TextFieldFormik
                      id="property_id"
                      label="Property Name"
                      className={classes.textField}
                      select
                      disabled={isSubmitting}
                      fullWidth
                    >
                      {insuredPropertiesOptions.map((option) => {
                        return (
                          <MenuItem key={option} value={option}>
                            {insuredPropertiesAndLocationsDict[option].desc}
                          </MenuItem>
                        );
                      })}
                    </TextFieldFormik>
                  </Grid>

                  <Grid item xs={3}>
                    <TextFieldFormik
                      id="employee_external_id"
                      label="Employee ID"
                      className={classes.textField}
                      fullWidth
                      disabled={isSubmitting}
                    />
                  </Grid>

                  <Grid item xs={3}>
                    <TextFieldFormik
                      id="employee_name"
                      label="Employee Name"
                      className={classes.textField}
                      fullWidth
                      disabled={isSubmitting}
                    />
                  </Grid>

                  <Grid item xs={3}>
                    <TextFieldFormik
                      id="employee_social_security_number"
                      label="Social Security number"
                      fullWidth
                      className={classes.textField}
                      InputProps={{
                        inputComponent: SocialSecurityNumberFormat,
                      }}
                      disabled={isSubmitting}
                    />
                  </Grid>

                  <Grid item xs={2}>
                    <div
                      style={{
                        display: 'flex',
                        justifyContent: 'center',
                        flexDirection: 'column',
                        height: '100%',
                        alignItems: 'center',
                      }}
                    >
                      <Button onClick={handleSubmit} variant="contained" color="primary">
                        Search
                      </Button>
                    </div>
                  </Grid>
                </Grid>
              </div>
            </CardDialog>
          </Grid>
        </Grid>
      )}
    </Formik>
  );
}

WCClaimsSearch.propTypes = {
  onSubmit: PropTypes.func.isRequired,
};

export default WCFnolSearchScreen;
